// Public: raise a complaint with the Grievance Officer, or check one's status by ticket number
// (IT Rules 2021; Consumer Protection (E-Commerce) Rules 2020). Works without signing in.
import React, { useState } from 'react';
import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Stack, TextField, MenuItem, Button, Alert, Chip, Grid, Divider, Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { SUBMIT_GRIEVANCE_MUTATION, GRIEVANCE_STATUS_QUERY, ME_QUERY } from '../graphql/queries';
import LegalFooter from '../components/LegalFooter';

const CATEGORIES = [
  ['booking', 'A booking or vendor service'], ['vendor', 'A vendor'], ['payment', 'A payment or refund'],
  ['profile_report', 'A member or profile'], ['fraud', 'Fraud or a request for money'], ['dowry', 'A dowry demand'],
  ['content', 'Content on the platform'], ['privacy', 'My privacy or personal data'], ['account', 'My account'], ['other', 'Something else'],
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const EMPTY = { name: '', email: '', phone: '', category: 'booking', subject: '', description: '' };

function Status({ g }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Typography fontWeight={700}>{g.ticketNumber}</Typography>
        <Chip size="small" label={g.status.replace('_', ' ')} color={['resolved', 'closed'].includes(g.status) ? 'success' : 'warning'} />
      </Stack>
      <Typography variant="body2" mb={1}>{g.subject}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Raised {fmt(g.createdAt)} · acknowledgement due {fmt(g.ackDueAt)} · resolution due {fmt(g.resolveDueAt)}
      </Typography>
      {g.timeline.map((e, i) => (
        <Typography key={i} variant="body2" sx={{ mt: 0.5 }}>
          <b>{fmt(e.at)}</b> — {e.status?.replace('_', ' ')}{e.message ? `: ${e.message}` : ''}
        </Typography>
      ))}
      {['resolved', 'closed'].includes(g.status) && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Not satisfied? You may appeal to the Grievance Appellate Committee (gac.gov.in) within 30 days of this decision.
        </Typography>
      )}
    </Paper>
  );
}

export default function GrievancePage() {
  const { data: me } = useQuery(ME_QUERY, { skip: !localStorage.getItem('token'), errorPolicy: 'ignore' });
  const signedIn = Boolean(me?.me);
  const [form, setForm] = useState(EMPTY);
  const [lookup, setLookup] = useState({ ticketNumber: '', email: '' });
  const [submit, { data: sent, loading, error }] = useMutation(SUBMIT_GRIEVANCE_MUTATION);
  const [check, { data: found, loading: checking, called }] = useLazyQuery(GRIEVANCE_STATUS_QUERY, { fetchPolicy: 'network-only' });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fdf6ff', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, flex: 1 }}>
        <Button component={RouterLink} to="/legal/notices" startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 1 }}>Legal Notices</Button>
        <Paper elevation={2} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
          <Typography variant="h4" fontWeight={700}>Raise a complaint</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Our Grievance Officer acknowledges complaints within 24 hours (48 hours for bookings and payments) and resolves them within 15 days
            (one month for bookings and payments). You will get a ticket number to follow it. Officer details are in the{' '}
            <Link component={RouterLink} to="/legal/notices">Legal Notices</Link>. In an emergency or if you have lost money to fraud, call <b>1930</b> or report at <b>cybercrime.gov.in</b>.
          </Typography>

          {sent ? (
            <Alert severity="success">
              Your complaint is registered: ticket <b>{sent.submitGrievance.ticketNumber}</b>. We have emailed you a copy.
              We will acknowledge it by {fmt(sent.submitGrievance.ackDueAt)}.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {error && <Grid item xs={12}><Alert severity="error">{error.message}</Alert></Grid>}
              {!signedIn && (
                <>
                  <Grid item xs={12} sm={4}><TextField label="Your name *" value={form.name} onChange={set('name')} fullWidth /></Grid>
                  <Grid item xs={12} sm={4}><TextField label="Email *" type="email" value={form.email} onChange={set('email')} fullWidth /></Grid>
                  <Grid item xs={12} sm={4}><TextField label="Phone" value={form.phone} onChange={set('phone')} fullWidth /></Grid>
                </>
              )}
              <Grid item xs={12} sm={5}>
                <TextField select label="It is about" value={form.category} onChange={set('category')} fullWidth>
                  {CATEGORIES.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={7}><TextField label="Subject *" value={form.subject} onChange={set('subject')} fullWidth /></Grid>
              <Grid item xs={12}>
                <TextField label="What happened? *" value={form.description} onChange={set('description')} fullWidth multiline minRows={4}
                  helperText="Include dates, names, booking or contract numbers, and what you would like us to do." />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" disabled={loading} onClick={() => submit({ variables: { input: form } })}>{loading ? 'Sending…' : 'Submit complaint'}</Button>
              </Grid>
            </Grid>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" fontWeight={700} mb={1}>Check a complaint</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" label="Ticket number" value={lookup.ticketNumber} onChange={e => setLookup(l => ({ ...l, ticketNumber: e.target.value }))} />
            <TextField size="small" label="Email used" value={lookup.email} onChange={e => setLookup(l => ({ ...l, email: e.target.value }))} />
            <Button variant="outlined" disabled={checking || !lookup.ticketNumber || !lookup.email} onClick={() => check({ variables: lookup })}>Check</Button>
          </Stack>
          {called && !checking && !found?.grievanceStatus && <Alert severity="info" sx={{ mt: 2 }}>No complaint matches that ticket number and email.</Alert>}
          {found?.grievanceStatus && <Status g={found.grievanceStatus} />}
        </Paper>
      </Container>
      <LegalFooter />
    </Box>
  );
}
